import { Phone, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import React from "react";
import { Participant } from "types";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { PhoneWithTooltip } from "@/components/MessageDelivery/PhoneWithTooltip";
import { RecipientEntry, RecipientOverflowList } from "@/components/PurchasesMenu/Timeline/Items/RecipientOverflowList";
import {
    DeliveryRecipient,
    formatDeliveryTime,
    getDeliveryIndicatorStyle,
    getDeliveryStatusText,
    getRolledUpDelivery,
} from "@/models/DeliveryReport";

interface DeliveryRecipientLabelProps {
    recipient: DeliveryRecipient;
    /** Off inside another tooltip, since ui-kit tooltips do not open from within one. */
    hasRawNumberTooltip?: boolean;
}

const DeliveryRecipientLabel = ({ recipient, hasRawNumberTooltip = false }: DeliveryRecipientLabelProps) => {
    const { report, name, phone } = recipient;
    const { sentTo } = report;
    const plainPhone = phone ? <Phone>{phone.value}</Phone> : sentTo;

    return (
        <NoTranslate>
            {name && `${name} `}
            {hasRawNumberTooltip ? <PhoneWithTooltip phone={phone} fallback={sentTo} /> : plainPhone}
        </NoTranslate>
    );
};

interface SmsRecipientsProps {
    recipients: DeliveryRecipient[];
    unreportedRecipients: Participant[];
}

export const SmsRecipients = ({ recipients, unreportedRecipients }: SmsRecipientsProps) => {
    const reportedEntries: RecipientEntry[] = recipients.map((recipient) => ({
        key: recipient.report.id,
        label: <DeliveryRecipientLabel hasRawNumberTooltip recipient={recipient} />,
        tooltipLabel: <DeliveryRecipientLabel recipient={recipient} />,
    }));
    const unreportedEntries: RecipientEntry[] = unreportedRecipients.map(({ id, name }) => {
        return { key: id, label: <NoTranslate>{name}</NoTranslate> };
    });

    return (
        <>
            To:&nbsp;
            <RecipientOverflowList recipients={[...reportedEntries, ...unreportedEntries]} />
        </>
    );
};

const DeliveryStatusTooltip = ({ recipients }: { recipients: DeliveryRecipient[] }) => {
    return (
        <ul className="text-sm">
            {recipients.map((recipient) => (
                <li key={recipient.report.id}>
                    <DeliveryRecipientLabel recipient={recipient} />: {getDeliveryStatusText(recipient.report)}
                </li>
            ))}
        </ul>
    );
};

export const SmsDeliveryStatus = ({ recipients }: { recipients: DeliveryRecipient[] }) => {
    const delivery = getRolledUpDelivery(recipients.map(({ report }) => report));
    if (!delivery) {
        return null;
    }

    const { icon: Icon, className } = getDeliveryIndicatorStyle(delivery.indicator);

    return (
        <Tooltip placement="top" content={<DeliveryStatusTooltip recipients={recipients} />}>
            <span
                data-testid="message-timeline-item-delivery-status"
                className="flex items-center gap-1 text-sm text-gray-dark"
            >
                <span>{formatDeliveryTime(delivery.time)}</span>
                {" • "}
                <Icon className={clsx("shrink-0", className)} />
            </span>
        </Tooltip>
    );
};
